import React from 'react';
import PropTypes from 'prop-types';
import { Form, Col } from 'react-bootstrap';
import clsx from 'clsx';
import { Typography, Checkbox, FormControlLabel } from '@material-ui/core';
import { ReactComponent as CheckIcon } from 'assets/check.svg';
import { ReactComponent as UnCheckIcon } from 'assets/uncheck.svg';
import Select from 'components/widgets/Select';
import InputNumber from 'components/widgets/InputNumber';
import DatePicker from 'react-datepicker';
import CalendarContainer from 'components/widgets/form/CalendarContainer';
import NumberUtils from 'lib/NumberUtils';
import DateUtils from 'lib/DateUtils';
import { isValidField } from 'utils/schema/utils';

const cpiFrequencyOptions = [
    { label: 'Weekly', value: 'WEEKLY' },
    { label: 'Bi-Weekly', value: 'BI-WEEKLY' },
    { label: 'Semi-Monthly', value: 'SEMI-MONTHLY' },
    { label: 'Monthly', value: 'MONTHLY' },
];

const getCPIFrequencyLabel = (frequency) => {
    const selectedOption = cpiFrequencyOptions.find((option) => option.value?.toUpperCase() === frequency?.toUpperCase());
    return selectedOption ? selectedOption.label : '';
};

const getCurrencyValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return NumberUtils.applyCurrencyFormat(value);
};

const getDateValue = (value) => {
    if (!value) return '';
    return DateUtils.getOnlyDate(value);
};

const DealCPITabContent = ({
    classes,
    cpiEditingMode,
    cpiRecord,
    cpiDraft,
    onChangeCPIDraft,
    dealStructure,
    cpiValidationErrors,
}) => {
    const invalidDescription = isValidField(cpiValidationErrors, 'description');
    const invalidAmount = isValidField(cpiValidationErrors, 'amount');
    const invalidFrequency = isValidField(cpiValidationErrors, 'frequency');
    const invalidFirstDueDate = isValidField(cpiValidationErrors, 'firstDueDate');
    const invalidLastDueDate = isValidField(cpiValidationErrors, 'lastDueDate');

    if (!cpiEditingMode) {
        return (
            <div className={classes.cpiContainer}>
                <Form>
                    <Form.Row className={classes.cpiRow}>
                        <Form.Group as={Col}>
                            <div className={classes.cpiField}>
                                <Typography className={clsx(classes.cpiLabel, classes.cpiReadOnlyLabel)}>Description:</Typography>
                                <Typography
                                    className={clsx(classes.cpiReadOnlyValue, { 'invalid-field': invalidDescription })}
                                    style={invalidDescription ? { color: '#dc3545' } : null}
                                >
                                    {cpiRecord.description || ''}
                                </Typography>
                            </div>
                        </Form.Group>
                        <Form.Group as={Col}>
                            <div className={classes.cpiColumnStack}>
                                <div className={classes.cpiField}>
                                    <Typography className={classes.cpiReadOnlyValue}>
                                        {cpiRecord.collectFirstCPIPaymentAtSigning
                                            ? <CheckIcon className={classes.productsCheckIcon} />
                                            : <UnCheckIcon className={classes.productsCheckIcon} />}
                                    </Typography>
                                    <Typography className={classes.cpiLabel}>Collect First CPI Payment at Signing:</Typography>
                                </div>
                                <div className={classes.cpiField}>
                                    <Typography className={clsx(classes.cpiLabel, classes.cpiReadOnlyLabel)}>Amount Due at Signing:</Typography>
                                    <Typography className={classes.cpiReadOnlyValue}>{getCurrencyValue(cpiRecord.amountDueAtSigning)}</Typography>
                                </div>
                            </div>
                        </Form.Group>
                    </Form.Row>

                    <Form.Row className={classes.cpiRow}>
                        <Form.Group as={Col}>
                            <div className={classes.cpiField}>
                                <Typography className={clsx(classes.cpiLabel, classes.cpiReadOnlyLabel)}>Frequency:</Typography>
                                <Typography
                                    className={clsx(classes.cpiReadOnlyValue, { 'invalid-field': invalidFrequency })}
                                    style={invalidFrequency ? { color: '#dc3545' } : null}
                                >
                                    {getCPIFrequencyLabel(cpiRecord.frequency)}
                                </Typography>
                            </div>
                        </Form.Group>
                        <Form.Group as={Col}>
                            <div className={classes.cpiField}>
                                <Typography className={clsx(classes.cpiLabel, classes.cpiReadOnlyLabel)}>First Due Date:</Typography>
                                <Typography
                                    className={clsx(classes.cpiReadOnlyValue, { 'invalid-field': invalidFirstDueDate })}
                                    style={invalidFirstDueDate ? { color: '#dc3545' } : null}
                                >
                                    {getDateValue(cpiRecord.firstDueDate)}
                                </Typography>
                            </div>
                        </Form.Group>
                    </Form.Row>

                    <Form.Row className={classes.cpiRow}>
                        <Form.Group as={Col}>
                            <div className={classes.cpiField}>
                                <Typography className={clsx(classes.cpiLabel, classes.cpiReadOnlyLabel)}>Amount:</Typography>
                                <Typography
                                    className={clsx(classes.cpiReadOnlyValue, { 'invalid-field': invalidAmount })}
                                    style={invalidAmount ? { color: '#dc3545' } : null}
                                >
                                    {getCurrencyValue(cpiRecord.amount)}
                                </Typography>
                            </div>
                        </Form.Group>
                        <Form.Group as={Col}>
                            <div className={classes.cpiField}>
                                <Typography className={clsx(classes.cpiLabel, classes.cpiReadOnlyLabel)}>Last Due Date:</Typography>
                                <Typography
                                    className={clsx(classes.cpiReadOnlyValue, { 'invalid-field': invalidLastDueDate })}
                                    style={invalidLastDueDate ? { color: '#dc3545' } : null}
                                >
                                    {getDateValue(cpiRecord.lastDueDate)}
                                </Typography>
                            </div>
                        </Form.Group>
                    </Form.Row>
                </Form>
            </div>
        );
    }

    return (
        <div className={classes.cpiContainer}>
            <Form>
                <Form.Row className={classes.cpiRow}>
                    <Form.Group as={Col}>
                        <div className={classes.cpiFieldEdit}>
                            <Typography className={classes.cpiLabel}>Description:</Typography>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                maxLength={50}
                                value={cpiDraft.description}
                                onChange={(event) => onChangeCPIDraft('description', event.target.value)}
                                size="sm"
                                className={isValidField(cpiValidationErrors, 'description') ? 'invalid-field' : ''}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group as={Col} className={classes.saleLineContainer}>
                        <div className={classes.cpiColumnStack}>
                            <div className={classes.cpiCheckboxRow}>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            className={classes.productCheckBox}
                                            checked={cpiDraft.collectFirstCPIPaymentAtSigning}
                                            color="primary"
                                            onChange={(event) => onChangeCPIDraft('collectFirstCPIPaymentAtSigning', event.target.checked)}
                                        />
                                    )}
                                    label="Collect First CPI Payment at Signing"
                                />
                            </div>
                            <div className={classes.cpiFieldEdit}>
                                <Typography className={classes.cpiLabel}>Amount Due at Signing:</Typography>
                                <InputNumber
                                    value={cpiDraft.amountDueAtSigning}
                                    onChange={(value) => onChangeCPIDraft('amountDueAtSigning', value)}
                                    showCurrency
                                    thousandSeparator
                                    placeholder="$0.00"
                                    size="sm"
                                    disabled={!cpiDraft.collectFirstCPIPaymentAtSigning}
                                />
                            </div>
                        </div>
                    </Form.Group>
                </Form.Row>

                <Form.Row className={classes.cpiRow}>
                    <Form.Group as={Col}>
                        <div className={classes.cpiFieldEdit}>
                            <Typography className={classes.cpiLabel}>Frequency:</Typography>
                            <Select
                                className={clsx(classes.cpiSelect, { 'invalid-field': invalidFrequency })}
                                name="frequency"
                                value={cpiDraft.frequency?.toUpperCase()}
                                onChange={(_, value) => onChangeCPIDraft('frequency', value)}
                                options={cpiFrequencyOptions}
                                placeholder="Select"
                                size="sm"
                            />
                        </div>
                    </Form.Group>
                    <Form.Group as={Col}>
                        <div className={classes.cpiFieldEdit}>
                            <Typography className={classes.cpiLabel}>First Due Date:</Typography>
                            <DatePicker
                                className={clsx('form-control', 'form-control-sm', { 'invalid-field': invalidFirstDueDate })}
                                onChange={(date) => onChangeCPIDraft('firstDueDate', date)}
                                selected={cpiDraft.firstDueDate}
                                popperContainer={CalendarContainer}
                                minDate={dealStructure.soldDate}
                            />
                        </div>
                    </Form.Group>
                </Form.Row>

                <Form.Row className={classes.cpiRow}>
                    <Form.Group as={Col}>
                        <div className={classes.cpiFieldEdit}>
                            <Typography className={classes.cpiLabel}>Amount:</Typography>
                            <InputNumber
                                className={invalidAmount ? 'invalid-field' : ''}
                                value={cpiDraft.amount}
                                onChange={(value) => onChangeCPIDraft('amount', value)}
                                showCurrency
                                thousandSeparator
                                placeholder="$0.00"
                                size="sm"
                            />
                        </div>
                    </Form.Group>
                    <Form.Group as={Col}>
                        <div className={classes.cpiFieldEdit}>
                            <Typography className={classes.cpiLabel}>Last Due Date:</Typography>
                            <DatePicker
                                className={clsx('form-control', 'form-control-sm', { 'invalid-field': invalidLastDueDate })}
                                onChange={(date) => onChangeCPIDraft('lastDueDate', date)}
                                selected={cpiDraft.lastDueDate}
                                popperContainer={CalendarContainer}
                                minDate={cpiDraft.firstDueDate || dealStructure.soldDate}
                                maxDate={dealStructure.finalPaymentDue}
                            />
                        </div>
                    </Form.Group>
                </Form.Row>
            </Form>
        </div>
    );
};

DealCPITabContent.propTypes = {
    classes: PropTypes.oneOfType([PropTypes.object]).isRequired,
    cpiEditingMode: PropTypes.bool.isRequired,
    cpiRecord: PropTypes.shape({
        description: PropTypes.string,
        collectFirstCPIPaymentAtSigning: PropTypes.bool,
        amountDueAtSigning: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        frequency: PropTypes.string,
        firstDueDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lastDueDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    }).isRequired,
    cpiDraft: PropTypes.shape({
        description: PropTypes.string,
        collectFirstCPIPaymentAtSigning: PropTypes.bool,
        amountDueAtSigning: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        frequency: PropTypes.string,
        firstDueDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lastDueDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    }).isRequired,
    onChangeCPIDraft: PropTypes.func.isRequired,
    dealStructure: PropTypes.shape({
        soldDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        finalPaymentDue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    }).isRequired,
    cpiValidationErrors: PropTypes.arrayOf(PropTypes.shape({
        field: PropTypes.string,
        message: PropTypes.string,
    })),
};

DealCPITabContent.defaultProps = {
    cpiValidationErrors: [],
};

export default DealCPITabContent;
